import React from 'react';
import styled from 'styled-components';
import {
  BarChart3,
  Sparkles,
  // TrendingUp
} from 'lucide-react';
import { theme } from '../styles/theme';
import { ROIAnalysis, ExportSummary } from '../types';
import ROIAnalysisCard from './ROIAnalysisCard';
import ExportSummaryTable from './ExportSummaryTable';

const AnalysisContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[4]};
`;

// const OverviewSection = styled.div`
//   background: linear-gradient(135deg, ${theme.colors.accent[100]}20, ${theme.colors.primary[100]}20);
//   border: 1px solid ${theme.colors.accent[500]}40;
//   border-radius: ${theme.borderRadius.lg};
//   padding: ${theme.spacing[6]};
//   position: relative;
//   overflow: hidden;

//   &::before {
//     content: '';
//     position: absolute;
//     top: -50%;
//     right: -50%;
//     width: 200px;
//     height: 200px;
//     background: radial-gradient(circle, ${theme.colors.accent[500]}20 0%, transparent 70%);
//     border-radius: 50%;
//   }
// `;

// const OverviewHeader = styled.div`
//   display: flex;
//   align-items: center;
//   gap: ${theme.spacing[3]};
//   margin-bottom: ${theme.spacing[4]};
//   position: relative;
//   z-index: 1;
// `;

// const OverviewIcon = styled.div`
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   width: 56px;
//   height: 56px;
//   background: linear-gradient(135deg, ${theme.colors.accent[500]}, ${theme.colors.primary[500]});
//   border-radius: ${theme.borderRadius.md};
//   color: white;
//   box-shadow: ${theme.shadows.md};
// `;

// const OverviewContent = styled.div`
//   position: relative;
//   z-index: 1;

//   h2 {
//     margin: 0;
//     color: ${theme.colors.textPrimary};
//     font-weight: ${theme.typography.fontWeight.bold};
//     font-size: ${theme.typography.fontSize.xl};
//   }

//   p {
//     margin: ${theme.spacing[1]} 0 0 0;
//     color: ${theme.colors.textSecondary};
//     font-size: ${theme.typography.fontSize.base};
//   }
// `;

const ProcessingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.accent[50]};
  border: 1px solid ${theme.colors.accent[200]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.accent[700]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  margin-bottom: ${theme.spacing[4]};
`;

const ProcessingDot = styled.div`
  width: 8px;
  height: 8px;
  background: ${theme.colors.accent[500]};
  border-radius: ${theme.borderRadius.full};
  animation: pulse 2s infinite;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[10]};
  text-align: center;
  color: ${theme.colors.textMuted};
  background: ${theme.colors.backgroundSecondary};
  border: 2px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${theme.colors.accent[100]};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[4]};
  color: ${theme.colors.accent[500]};
`;

const EmptyTitle = styled.h3`
  margin: 0 0 ${theme.spacing[2]} 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.lg};
`;

const EmptyDescription = styled.p`
  margin: 0;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  max-width: 400px;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

interface PerformanceAnalysisResultsProps {
  roiAnalysis?: ROIAnalysis;
  exportSummary?: ExportSummary;
  isProcessing?: boolean;
  /** GENERATE mode = drafts authored from scratch, no baseline to compare.
   *  Hides the ROI card (which would otherwise show all-zero figures or
   *  "Pending") and replaces it with an explanatory notice. */
  isGenerateMode?: boolean;
}

const NoBaselineNotice = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[5]};
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.accent[50]});
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.textPrimary};
`;

const NoBaselineIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.md};
  color: white;
  flex-shrink: 0;
`;

const NoBaselineBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};

  h3 {
    margin: 0;
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.semibold};
  }

  p {
    margin: 0;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.sm};
    line-height: ${theme.typography.lineHeight.relaxed};
  }
`;

const PerformanceAnalysisResults: React.FC<PerformanceAnalysisResultsProps> = ({
  roiAnalysis,
  exportSummary,
  isProcessing = false,
  isGenerateMode = false,
}) => {
  // Always show content when processing or when we have data
  const shouldShowContent = isProcessing || roiAnalysis || exportSummary;

  if (!shouldShowContent) {
    return (
      <AnalysisContainer>
        <EmptyState>
          <EmptyIcon>
            <BarChart3 size={32} />
          </EmptyIcon>
          <EmptyTitle>Performance Analysis Pending</EmptyTitle>
          <EmptyDescription>
            AI will analyze the impact of response refinements and generate comprehensive
            performance metrics once the enhancement process is complete. This includes
            ROI calculations, improvement statistics, and exportable summaries.
          </EmptyDescription>
        </EmptyState>
      </AnalysisContainer>
    );
  }

  return (
    <AnalysisContainer>
      {isProcessing && (
        <ProcessingIndicator>
          <ProcessingDot />
          Analyzing performance impact and generating export summaries...
        </ProcessingIndicator>
      )}

      {/* GENERATE mode: there is no prior baseline to compare against — the
          drafts were authored from scratch from the company knowledge base.
          Suppress the ROI card (which would render all-zero or NaN values)
          and explain why. The Export Summary below still surfaces useful
          per-question stats (length, score, sections covered). */}
      {isGenerateMode ? (
        <NoBaselineNotice>
          <NoBaselineIcon>
            <Sparkles size={20} />
          </NoBaselineIcon>
          <NoBaselineBody>
            <h3>Drafts authored from scratch — no baseline to compare</h3>
            <p>
              This run was started in <strong>GENERATE mode</strong>: the source
              document had no inline responses, so AI authored each answer from
              your knowledge base. There is no prior version to score against,
              so the ROI / improvement view doesn't apply here. Use the
              Question Matrix to review and edit each draft, and see the
              Export Summary below for per-question stats.
            </p>
          </NoBaselineBody>
        </NoBaselineNotice>
      ) : (
        <ROIAnalysisCard
          roiAnalysis={roiAnalysis || {
            percentage_improvement: 0,
            baseline_score: 0,
            enhanced_score: 0,
            total_questions_improved: 0,
            high_impact_improvements: 0,
            summary: isProcessing ? 'Calculating performance improvements...' : '',
            confidence_level: 'Medium'
          }}
          isLoading={isProcessing && !roiAnalysis}
        />
      )}

      {/* Export Summary - show loading state or actual data */}
      <ExportSummaryTable
        exportSummary={exportSummary || {
          generated_on: new Date().toISOString(),
          total_refinements: 0,
          average_improvement: 0,
          high_quality_responses: 0,
          sections_improved: [],
          refinement_statistics: []
        }}
        isLoading={isProcessing && !exportSummary}
        isGenerateMode={isGenerateMode}
      />
    </AnalysisContainer>
  );
};

export default PerformanceAnalysisResults;